import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { FetchPartnershipsIfNeeded } from '@wilson/clients/states';
import { FeatureFlagsService } from '@wilson/feature-flags';
import { FetchLabelsIfNeeded } from '@wilson/labels';
import { FetchProjectsIfNeededState } from '@wilson/projects/state';
import {
  GanttDimensionDataSourceService,
  OvertimeDataSourceService,
  ProductivityDataSourceService,
  ShiftMultiSelectHelperService,
  UserKpisDataSourceService,
  ValidationDataSourceService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  ResetShiftTimelineWidth,
  ShiftTimelineFilterStateService,
  SubscribeToKpiRequests,
  TimelineStaysSuggestionService,
  UnassignedRegionVisibleShiftsStateService,
  UnsubscribeFromKpiRequests,
  UpdateWindowWidthHeightToWindowInnerWidthHeight,
} from '@wilson/shift-timeline/state';
import { SideNavSettingsState } from '@wilson/side-nav-settings/state';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import {
  filter,
  map,
  skip,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { NewShiftDropZoneHelperService } from '../../components-v2/new-shift-drop-zone-component-v2/new-shift-drop-zone-helper.service';
import { ShiftTemplateDropZoneHelperService } from '../../components-v2/shift-template-drop-zone-v2/shift-template-drop-zone-helper.service';
import { TimelineUserRowContextMenuWrapperService } from '../../components-v2/timeline-user-row-shifts-v2/timeline-user-row-context-menu-wrapper/timeline-user-row-context-menu-wrapper.service';
import { ShiftTimelineGanttDimensionDataSourceService } from '../../services/shift-timeline-gantt-dimension-data-source.service';
import { ShiftTimelineValidationDataSourceService } from '../../services/shift-timeline-validation-data-source.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { UsersProductivityDataSourceService } from '../../services/users-productivity-data-source.service';
import { ShiftTimelineKpiDataSourceServiceService } from '../../shift-timeline-kpi-data-source-service.service';
import { ShiftTimelineOvertimeDataSourceServiceService } from '../../shift-timeline-overtime-data-source-service.service';
import { NewAbsenceDropZoneService } from '../new-absence-drop-zone/new-absence-drop-zone.service';
import { HorizontalScrollingManagerService } from '../shift-timeline-gantt-chart/horizontal-scrolling-manager.service';
import { ShiftTimelineService } from './shift-timeline.service';
import { TimelinePresenceService } from '@wilson/non-domain-specific/realtime-collaboration/live-viewers';

@Component({
  selector: 'wilson-shift-timeline',
  templateUrl: './shift-timeline.component.html',
  styleUrls: ['./shift-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ValidationDataSourceService,
      useClass: ShiftTimelineValidationDataSourceService,
    },
    {
      provide: OvertimeDataSourceService,
      useClass: ShiftTimelineOvertimeDataSourceServiceService,
    },
    {
      provide: UserKpisDataSourceService,
      useClass: ShiftTimelineKpiDataSourceServiceService,
    },
    {
      provide: GanttDimensionDataSourceService,
      useClass: ShiftTimelineGanttDimensionDataSourceService,
    },
    {
      provide: ProductivityDataSourceService,
      useClass: UsersProductivityDataSourceService,
    },
    UnassignedRegionVisibleShiftsStateService,
    TimelineStaysSuggestionService,
    ShiftTimelineService,
    NewShiftDropZoneHelperService,
    TimelineUserRowContextMenuWrapperService,
    NewAbsenceDropZoneService,
    ShiftTemplateDropZoneHelperService,
    HorizontalScrollingManagerService,
  ],
  standalone: false,
})
export class ShiftTimelineComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly horizontalScrollingManagerService = inject(
    HorizontalScrollingManagerService,
  );
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelinePresenceService = inject(TimelinePresenceService);

  public isFilterDrawerVisible$ =
    this.shiftTimelineFilterStateService.showFilterDrawer$;

  @HostListener('window:focus')
  onFocus(): void {
    this.store.dispatch(new UpdateWindowWidthHeightToWindowInnerWidthHeight());
    this.horizontalScrollingManagerService.timelineAssignmentRegionWidthPxUpdateSubject.next(
      new Date(),
    );
  }

  @HostListener('window:resize')
  onResize(): void {
    this.store.dispatch(new UpdateWindowWidthHeightToWindowInnerWidthHeight());
    this.horizontalScrollingManagerService.timelineAssignmentRegionWidthPxUpdateSubject.next(
      new Date(),
    );
    this.timelineCommonOperationsService.updateScrollLeftPositionOnResize();
  }

  private takeUntilDestroyed$ = new Subject<void>();
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.timelinePresenceService.register();
    this.store.dispatch([new ResetShiftTimelineWidth()]);

    this.featureFlagsService.flag$
      .pipe(
        map(
          (flags: LDFlagSet) => flags['portal-shift-timeline-utilization-kpis'],
        ),
        takeUntil(this.takeUntilDestroyed$),
      )
      .subscribe((enabled) => {
        if (enabled) {
          this.store.dispatch(new SubscribeToKpiRequests());
        } else {
          this.store.dispatch(new UnsubscribeFromKpiRequests());
        }
      });

    this.store.dispatch(new FetchProjectsIfNeededState());
    this.store.dispatch(new FetchPartnershipsIfNeeded());
    this.store.dispatch(new FetchLabelsIfNeeded());

    // Set timeline scroll to the correct position when sidenav is toggled
    this.subscriptions.add(
      this.store
        .select(AuthState.userId)
        .pipe(filter((userId) => userId !== null))
        .pipe(
          switchMap((userId) =>
            this.store.select(
              SideNavSettingsState.getIsSideNavCollapsed(userId),
            ),
          ),
        )
        .pipe(skip(1)) // Skip the first value because on first load timeline scroll is already set to the correct position
        .subscribe(() => {
          this.timelineCommonOperationsService.updateScrollLeftPositionOnResize();
        }),
    );
  }

  ngOnDestroy(): void {
    this.timelinePresenceService.unregister();
    this.store.dispatch(new UnsubscribeFromKpiRequests());
    this.shiftTimelineFilterStateService.showFilterDrawer = false;
    this.shiftMultiSelectHelperService.clearSelection();
    this.subscriptions.unsubscribe();
    this.takeUntilDestroyed$.next();
    this.takeUntilDestroyed$.complete();
  }
}
